import { useCallback, useEffect, useMemo, useState } from "react";
import { type ModifierState, normalizeCombo } from "@/domain/profile-resolver/index";
import { modifierFamily } from "@/hardware/modifier-family";
import { isEditableTarget } from "@/platform/lib/editable-target";

/** Fold held and latched modifier codes into the exact-combo ModifierState. */
export function computeMods(codes: Iterable<string>): ModifierState {
  const mods: ModifierState = {};
  for (const code of codes) {
    const family = modifierFamily(code);
    if (family) mods[family] = true;
  }
  return mods;
}

/**
 * Held physical keys + latched modifier families for the visualizer. `held` is every physically held
 * KeyboardEvent.code (window keydown/keyup, cleared on blur so alt-tab cannot strand one); it is the
 * single source the design uses (keylens-app.jsx) for BOTH the press-feedback depress (a
 * cap stays down while its key is held) and, filtered to modifier families, the active-combo `mods`.
 * Latched codes (clicking a modifier cap) are sticky until toggled and fold into `mods` only.
 */
export function useModifiers() {
  const [held, setHeld] = useState<ReadonlySet<string>>(() => new Set());
  const [latched, setLatched] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // track EVERY held key (not just modifiers): the depress needs all of them; computeMods filters
      // to modifier families for the board-face switch
      if (isEditableTarget(e.target)) return;
      setHeld((prev) => (prev.has(e.code) ? prev : new Set(prev).add(e.code)));
    };
    // releases are never gated: a keyup swallowed while focus sat in a form
    // field would otherwise leave the modifier stuck held
    const up = (e: KeyboardEvent) => {
      setHeld((prev) => {
        if (!prev.has(e.code)) return prev;
        const next = new Set(prev);
        next.delete(e.code);
        return next;
      });
    };
    const blur = () => setHeld((prev) => (prev.size ? new Set<string>() : prev));
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, []);

  const toggleLatch = useCallback((code: string) => {
    setLatched((prev) => {
      const next = new Set(prev);
      if (!next.delete(code)) next.add(code);
      return next;
    });
  }, []);

  // the modifier-layer strip latches a whole family: toggling Shift latches the
  // family's representative code, which combines with others into the exact combo
  const toggleModifier = useCallback(
    (family: "ctrl" | "alt" | "shift") => toggleLatch(MOD_FAMILY_CODE[family]),
    [toggleLatch],
  );

  // the strip's Base pill clears every latch (held physical modifiers are untouched)
  const clearLatched = useCallback(() => {
    setLatched((prev) => (prev.size ? new Set<string>() : prev));
  }, []);

  // latch exactly one combo's families (a readout chip click): the board jumps to
  // that modifier layer. "" clears all, like Base
  const latchCombo = useCallback((combo: string) => {
    const families = normalizeCombo(combo).split("+").filter(Boolean);
    setLatched(new Set(families.map((f) => MOD_FAMILY_CODE[f as "ctrl" | "alt" | "shift"])));
  }, []);

  // `held` gets a new Set identity on every keypress (it tracks all keys for the depress), so
  // memoizing `mods` on it directly would hand the resolver a fresh object each keystroke and
  // re-fire the whole [layers, mods] render chain. Key the memo on the three modifier booleans
  // instead: a non-modifier press leaves them unchanged, so `mods` keeps its reference and only a
  // real modifier toggle invalidates it.
  const heldMods = computeMods([...held, ...latched]);
  const ctrl = heldMods.ctrl === true;
  const alt = heldMods.alt === true;
  const shift = heldMods.shift === true;
  const mods = useMemo<ModifierState>(() => {
    const m: ModifierState = {};
    if (ctrl) m.ctrl = true;
    if (alt) m.alt = true;
    if (shift) m.shift = true;
    return m;
  }, [ctrl, alt, shift]);

  // `held` (raw codes) feeds the press-feedback depress; `mods` is its modifier-family projection
  return { mods, held, toggleLatch, toggleModifier, clearLatched, latchCombo };
}

/** The representative left-hand code a family latches from the modifier strip. */
const MOD_FAMILY_CODE: Record<"ctrl" | "alt" | "shift", string> = {
  ctrl: "ControlLeft",
  alt: "AltLeft",
  shift: "ShiftLeft",
};
